/**
 * Combobox: a searchable picker for template inputs (countries, languages, storefronts, airports, years).
 *
 *   <Combobox id={id} value={["US"]} onChange={setValues} options={countries} />                 one value
 *   <Combobox id={id} multiple maxItems={5} value={["US", "GB"]} onChange={setValues} options={countries} />
 *   <Combobox id={id} value={codes} onChange={setCodes} load={q => searchAirports(q).then(toOptions)} />
 *   allowCustom={q => (/^[a-z]{2}-\w{1,3}$/i.test(q) ? q.toUpperCase() : null)}   typed values it may add
 *
 * The text box is a WAI-ARIA combobox: arrow keys move through the list, Enter picks, Escape closes (only the top
 * layer, via useLayer/useEscape), Backspace in an empty box removes the last chip. Typing filters on the label,
 * the value and any extra keywords, ignoring case and accents. In single mode the box shows the chosen label until
 * you type.
 */
import { ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { fieldClasses } from "@/components/ui/TextField";
import { useEscape, useLayer } from "@/hooks/useOverlay";
import { useFloating } from "./useFloating";

export interface ComboOption {
  value: string;
  label: string;
  /** Short code shown on the right (US, es, LHR). */
  hint?: string;
  /** Second, grey line (an airport's city and country). */
  detail?: string;
  /** Shorter label for a chip (an airport's city). */
  short?: string;
  keywords?: string;
  /** ISO country code for a flag. */
  flag?: string;
  /** Iconify name shown instead of a flag (a globe for Worldwide). */
  icon?: string;
}

interface ComboboxProps {
  id: string;
  value: string[];
  onChange: (values: string[]) => void;
  options?: ComboOption[];
  /** Async options for the typed text (airports). Called a moment after typing stops. */
  load?: (q: string) => Promise<ComboOption[]>;
  /** Options for values already chosen that the static list does not hold (airport labels). */
  known?: Record<string, ComboOption>;
  multiple?: boolean;
  maxItems?: number;
  /** A typed value the list may add as it is, or null. Shown as the last option. */
  allowCustom?: (q: string) => string | null;
  customLabel?: (v: string) => string;
  placeholder?: string;
  invalid?: boolean;
  describedBy?: string;
  required?: boolean;
  /** Shown when nothing matches. */
  emptyText?: string;
}

const norm = (s: string) =>
  s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

export function Flag({ code, icon }: { code?: string; icon?: string }) {
  if (icon) return <Icon icon={icon} className="shrink-0 text-base text-fg-muted" aria-hidden="true" />;
  if (!code || !/^[a-z]{2}$/i.test(code)) return null;
  return <span className={`fi fi-${code.toLowerCase()} shrink-0 rounded-[2px] shadow-[0_0_0_1px_rgb(0_0_0/0.06)]`} aria-hidden="true" />;
}

function filterOptions(options: ComboOption[], q: string): ComboOption[] {
  const n = norm(q);
  if (!n) return options;
  const scored: [number, ComboOption][] = [];
  for (const o of options) {
    const label = norm(o.label);
    const value = norm(o.value);
    let s = 0;
    if (value === n || norm(o.hint ?? "") === n) s = 4;
    else if (label.startsWith(n)) s = 3;
    else if (label.split(/[\s(]+/).some(w => w.startsWith(n))) s = 2;
    else if (label.includes(n) || norm(o.keywords ?? "").includes(n) || value.startsWith(n)) s = 1;
    if (s) scored.push([s, o]);
  }
  return scored.sort((a, b) => b[0] - a[0]).map(([, o]) => o);
}

export default function Combobox({
  id,
  value,
  onChange,
  options = [],
  load,
  known = {},
  multiple = false,
  maxItems,
  allowCustom,
  customLabel = v => `Use "${v}"`,
  placeholder,
  invalid,
  describedBy,
  required,
  emptyText = "No matches",
}: ComboboxProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState<ComboOption[]>([]);
  const [loading, setLoading] = useState(false);
  const isTop = useLayer(open);
  const floating = useFloating(open, fieldRef, listRef, { minWidth: 256 });

  const byValue = useMemo(() => {
    const m: Record<string, ComboOption> = { ...known };
    for (const o of [...options, ...loaded]) m[o.value] = o;
    return m;
  }, [options, loaded, known]);

  const close = () => {
    setOpen(false);
    setQuery("");
    setTyped(false);
  };
  useEscape(open, close, isTop);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !listRef.current?.contains(t)) close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Async options, a moment after typing stops.
  useEffect(() => {
    if (!load || !open) return;
    const q = query.trim();
    if (q.length < 2) {
      setLoaded([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      load(q)
        .then(r => !cancelled && setLoaded(r))
        .catch(() => !cancelled && setLoaded([]))
        .finally(() => !cancelled && setLoading(false));
    }, 160);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open, load]);

  const q = typed ? query : "";
  const items: ComboOption[] = useMemo(() => {
    const base = load ? (q.trim().length >= 2 ? loaded : []) : filterOptions(options, q);
    const list = multiple ? base.filter(o => !value.includes(o.value)) : base;
    const custom = allowCustom && q.trim() ? allowCustom(q.trim()) : null;
    if (custom && !list.some(o => o.value === custom) && !value.includes(custom)) return [...list, { value: custom, label: customLabel(custom), icon: "lucide:plus" }];
    return list;
  }, [load, loaded, options, q, multiple, value, allowCustom, customLabel]);

  useEffect(() => setActive(0), [q, loaded]);
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const full = multiple && maxItems !== undefined && value.length >= maxItems;
  const selected = !multiple ? byValue[value[0]] : undefined;

  const pick = (o: ComboOption) => {
    if (multiple) {
      if (!full) onChange([...value, o.value]);
      setQuery("");
      setTyped(false);
      inputRef.current?.focus();
    } else {
      onChange([o.value]);
      close();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return setOpen(true);
      if (!items.length) return;
      setActive(a => (e.key === "ArrowDown" ? (a + 1) % items.length : (a - 1 + items.length) % items.length));
    } else if (e.key === "Enter") {
      if (open && items[active]) {
        e.preventDefault();
        pick(items[active]);
      }
    } else if (e.key === "Backspace" && multiple && !query && value.length) {
      onChange(value.slice(0, -1));
    } else if (e.key === "Tab") {
      close();
    }
  };

  const chip = (v: string): ReactNode => {
    const o = byValue[v];
    return (
      <span key={v} className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-line bg-surface-muted py-0.5 pl-1.5 pr-0.5 text-13 text-fg">
        <Flag code={o?.flag} icon={o?.icon && o.icon !== "lucide:plus" ? o.icon : undefined} />
        <span className="truncate">
          {o?.hint ? <span className="font-medium">{o.hint}</span> : null}
          {o?.hint ? <span className="text-fg-muted"> {o.short ?? o.label}</span> : (o?.label ?? v)}
        </span>
        <button
          type="button"
          aria-label={`Remove ${o?.label ?? v}`}
          onClick={() => onChange(value.filter(x => x !== v))}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-muted outline-none hover:bg-line hover:text-fg focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Icon icon="lucide:x" className="text-xs" aria-hidden="true" />
        </button>
      </span>
    );
  };

  // Single mode keeps the chosen label in the box (selected on focus, so typing replaces it).
  const inputValue = typed ? query : multiple ? "" : (selected?.label ?? value[0] ?? "");
  const activeId = open && items[active] ? `${listId}-${active}` : undefined;

  return (
    <div ref={rootRef} className="relative">
      <div
        ref={fieldRef}
        className={`${fieldClasses(invalid)} flex min-h-9 cursor-text flex-wrap items-center gap-1.5 px-2.5 py-1 focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/20 ${invalid ? "focus-within:border-danger focus-within:ring-danger/20" : ""}`}
        onMouseDown={e => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        {multiple ? value.map(chip) : !typed && <Flag code={selected?.flag} icon={selected?.icon} />}
        {!full && (
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            aria-required={required || undefined}
            autoComplete="off"
            spellCheck={false}
            value={inputValue}
            placeholder={multiple && value.length ? "Add another" : (placeholder ?? "Search")}
            onChange={e => {
              setQuery(e.target.value);
              setTyped(true);
              setOpen(true);
            }}
            onFocus={e => {
              if (!multiple) e.currentTarget.select();
            }}
            onClick={() => setOpen(true)}
            onKeyDown={onKeyDown}
            className="h-7 min-w-[6rem] flex-1 bg-transparent text-base text-fg outline-none placeholder:text-fg-muted sm:text-sm"
          />
        )}
        {!multiple && selected?.hint && !typed && !open ? <span className="shrink-0 font-mono text-xs text-fg-muted">{selected.hint}</span> : null}
        {full && <span className="sr-only" id={id} tabIndex={-1}>{`${maxItems} chosen, the most this field takes`}</span>}
        <Icon icon={load ? "lucide:search" : "lucide:chevron-down"} className="pointer-events-none shrink-0 text-sm text-fg-muted" aria-hidden="true" />
      </div>

      {open &&
        createPortal(
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          style={{ ...floating, maxHeight: Math.min(288, Number(floating.maxHeight ?? 288)) }}
          className="overflow-y-auto rounded-control border border-line bg-raised p-1 shadow-popover"
        >
          {items.map((o, i) => {
            const isSel = value.includes(o.value);
            return (
              <li
                key={o.value}
                id={`${listId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={isSel}
                onMouseDown={e => e.preventDefault()}
                onClick={() => pick(o)}
                onMouseMove={() => active !== i && setActive(i)}
                className={`flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm ${i === active ? "bg-surface-muted" : ""} ${isSel ? "font-medium text-primary-text" : "text-fg"}`}
              >
                <Flag code={o.flag} icon={o.icon} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.detail ? <span className="block truncate text-xs text-fg-muted">{o.detail}</span> : null}
                </span>
                {o.hint ? <span className="shrink-0 font-mono text-xs text-fg-muted">{o.hint}</span> : null}
                {isSel ? <Icon icon="lucide:check" className="shrink-0 text-sm" aria-hidden="true" /> : null}
              </li>
            );
          })}
          {!items.length && (
            <li role="presentation" className="px-2.5 py-3 text-center text-13 text-fg-muted">
              {load && q.trim().length < 2 ? "Type a city, airport or code" : loading ? "Searching..." : emptyText}
            </li>
          )}
        </ul>,
          document.body,
        )}
    </div>
  );
}
